import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, X } from "lucide-react";
import { toast } from "sonner";
import useRetryFetch from "../../hooks/useRetryFetch";
import { config } from "../../Environment";
import { cn } from "../../lib/utils";

interface EditableEngagementNameProps {
  engagementName: string;
  processId: string;
  activityName?: string;
  onNameChange: (newName: string) => void;
  isMobile?: boolean;
}

export default function EditableEngagementName({
  engagementName,
  processId,
  activityName,
  onNameChange,
  isMobile = false,
}: EditableEngagementNameProps) {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(engagementName);
  const [showNudge, setShowNudge] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isGenericName = useCallback(() => {
    if (!engagementName) return false;
    // Pattern 1: "{Activity Name} - {date}" (from UnfinishedEngagementModal)
    if (activityName && engagementName.startsWith(`${activityName} -`)) return true;
    // Pattern 2: "Engagement {date}" (from direct start without dialog)
    if (/^Engagement\s+\d/.test(engagementName)) return true;
    return false;
  }, [activityName, engagementName]);

  // Show nudge after 2s delay if name looks generic and hasn't been dismissed
  useEffect(() => {
    if (!processId) return;
    const dismissKey = `engagement_name_nudge_dismissed_${processId}`;
    if (sessionStorage.getItem(dismissKey)) return;
    if (!isGenericName()) return;

    nudgeTimerRef.current = setTimeout(() => {
      setShowNudge(true);
    }, 2000);

    return () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, [processId, isGenericName]);

  // Sync editValue when engagementName changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(engagementName);
    }
  }, [engagementName, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const dismissNudge = useCallback(() => {
    setShowNudge(false);
    if (processId) {
      sessionStorage.setItem(
        `engagement_name_nudge_dismissed_${processId}`,
        "1"
      );
    }
  }, [processId]);

  const startEditing = () => {
    setIsEditing(true);
    setEditValue(engagementName);
    dismissNudge();
  };

  const saveEngagementName = async (newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === engagementName) {
      setIsEditing(false);
      setEditValue(engagementName);
      return;
    }

    // Optimistic update
    const previousName = engagementName;
    onNameChange(trimmed);
    setIsEditing(false);
    setIsSaving(true);

    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/process/${processId}/engagement-name`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ engagementName: trimmed }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update");
      }

      toast.success(t("engagement_name_saved") || "Name updated");
    } catch {
      // Revert on error
      onNameChange(previousName);
      setEditValue(previousName);
      toast.error(t("engagement_name_save_error") || "Failed to update name");
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEngagementName(editValue);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
      setEditValue(engagementName);
    }
  };

  if (!engagementName) return null;

  // Mobile variant: matches the primary header color
  if (isMobile) {
    return (
      <div className="px-3 py-1 bg-primary/90">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => saveEngagementName(editValue)}
            onKeyDown={handleKeyDown}
            placeholder={
              t("engagement_name_placeholder") || "e.g., My Monday reflection..."
            }
            className="w-full text-[11px] text-center bg-transparent border-b border-white/40 outline-none py-0.5 text-white placeholder:text-white/50"
          />
        ) : (
          <button
            onClick={startEditing}
            className="flex items-center justify-center w-full gap-1.5"
            disabled={isSaving}
          >
            <span className="text-[11px] text-white/80 font-medium">
              {engagementName}
            </span>
            <Pencil className="w-2.5 h-2.5 text-white/40 flex-shrink-0" />
          </button>
        )}

        {/* Nudge */}
        {showNudge && !isEditing && (
          <div className="flex items-center justify-center gap-1 mt-0.5 animate-in fade-in duration-300">
            <p className="text-[10px] text-white/70 italic">
              {t("name_your_engagement") ||
                "Give this a personal name \u2014 what\u2019s your intention?"}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissNudge();
              }}
              className="text-white/50 hover:text-white/80 flex-shrink-0"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop variant: inline in header
  return (
    <div className="w-full">
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveEngagementName(editValue)}
          onKeyDown={handleKeyDown}
          placeholder={
            t("engagement_name_placeholder") || "e.g., My Monday reflection..."
          }
          className="w-full text-xs text-center bg-transparent border-b border-primary/30 outline-none py-0.5 text-gray-600 placeholder:text-gray-400"
        />
      ) : (
        <button
          onClick={startEditing}
          className={cn(
            "flex items-center justify-center w-full gap-1 group cursor-pointer",
            isSaving && "opacity-60"
          )}
          disabled={isSaving}
        >
          <p className="text-xs text-center text-gray-600 line-clamp-1">
            {engagementName}
          </p>
          <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </button>
      )}

      {/* Nudge hint */}
      {showNudge && !isEditing && (
        <div className="flex items-center justify-center gap-1 animate-in fade-in duration-300">
          <p className="text-[10px] text-primary/70 italic">
            {t("name_your_engagement") ||
              "Give this a personal name \u2014 what\u2019s your intention?"}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              dismissNudge();
            }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
